import { useCallback, useEffect, useMemo, useReducer } from 'react';

import axios, { endpoints } from 'src/utils/axios';

import { UserStatus } from 'src/redux/slices/users';

import { AuthContext } from './auth-context';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';
import { isValidToken, setNotificationTokenToSession, setSession } from './utils';

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  LOGOUT = 'LOGOUT',
  SET_NOTIFICATION_TOKEN = 'SET_NOTIFICATION_TOKEN',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
    notificationToken: string | null;
  };
  [Types.LOGIN]: {
    user: AuthUserType;
  };
  [Types.REGISTER]: {
    user: AuthUserType;
  };
  [Types.LOGOUT]: undefined;
  [Types.SET_NOTIFICATION_TOKEN]: {
    notificationToken: string;
  };
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
  notificationToken: null,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
      notificationToken: action.payload.notificationToken,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
      notificationToken: null,
    };
  }
  if (action.type === Types.SET_NOTIFICATION_TOKEN) {
    return {
      ...state,
      notificationToken: action.payload.notificationToken,
    };
  }
  return state;
};

const STORAGE_KEY = 'accessToken';
export const NOTIFICATION_TOKEN_KEY = 'notificationToken';

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const removeNotificationToken = useCallback((userId: string, notificationToken: string) => {
    axios.put(endpoints.user.fcm(userId), {
      fcmToken: notificationToken,
    });
  }, []);

  const logout = useCallback(async () => {
    if (state.user && state.notificationToken) {
      removeNotificationToken(state.user?._id, state.notificationToken);
    }

    setSession(null);
    setNotificationTokenToSession(null);

    dispatch({
      type: Types.LOGOUT,
    });
  }, [removeNotificationToken, state]);

  const initialize = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEY);
      const notificationToken = localStorage.getItem(NOTIFICATION_TOKEN_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);

        const { data } = res.data;

        if (data.status !== UserStatus.ACTIVE) {
          logout();
        } else {
          dispatch({
            type: Types.INITIAL,
            payload: {
              user: {
                ...data,
              },
              notificationToken,
            },
          });
        }
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
            notificationToken,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
          notificationToken: null,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    const data = {
      email,
      password,
    };

    const res = await axios.post(endpoints.auth.login, data);

    const response = res.data;
    const { accessToken, ...user } = response.data;
    setSession(accessToken);

    dispatch({
      type: Types.LOGIN,
      payload: {
        user,
      },
    });
  }, []);

  const setNotificationToken = useCallback((notificationToken: string) => {
    setNotificationTokenToSession(notificationToken);

    dispatch({
      type: Types.SET_NOTIFICATION_TOKEN,
      payload: {
        notificationToken,
      },
    });
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {},
    []
  );

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'jwt',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      register,
      logout,
      setNotificationToken,
    }),
    [login, logout, register, state.user, status, setNotificationToken]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
